import { useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Menu as MenuIcon } from "@mui/icons-material"
import { format } from "date-fns"
import { ru } from "date-fns/locale"
import {
  AnalysisVersionSelect,
  AuthorsTable,
  ChatLibraryPanel,
  ContentTypesTable,
  FraudScore,
  HourlyBarChart,
  IntersectionSummary,
  KpiGrid,
  MetricsGlossary,
  ReactionsSection,
  SourceFilterChips,
  SummaryTable,
  WeekdayBarChart,
  WeeklyTrendChart,
} from "../components"
import {
  useGetAnalysisQuery,
  useGetChatAnalysesQuery,
} from "../../store/chatAnalyzeApi"
import type { ChatAnalyze } from "../interfaces"

// ── SectionPaper ──────────────────────────────────────────────────────────────

function SectionPaper({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          color: "info.main",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "info.main",
          opacity: 0.7,
        }}
      >
        {title}
      </Typography>
      {hint && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 0.75,
            bgcolor: "action.hover",
            borderLeft: "3px solid",
            borderColor: "info.main",
            borderRadius: "0 4px 4px 0",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ℹ {hint}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Paper>
  )
}

// ── ChatDetailPanel ───────────────────────────────────────────────────────────

/** Экспортирован для тестов фильтра источника истории анализов. */
export function ChatDetailPanel({
  chat,
  librarySource,
}: {
  chat: ChatAnalyze.SavedChat
  /** Source-фильтр библиотеки — кандидаты пересечений (дельта 2026-09-22 §1.1).
   *  Не путать с локальным source ниже — это фильтр истории анализов (§1.2). */
  librarySource?: string
}) {
  // Фильтр истории анализов по источнику (§1.2); undefined = без фильтра.
  const [source, setSource] = useState<string | undefined>(undefined)
  const { data: analyses = [] } = useGetChatAnalysesQuery({
    chatId: chat.id,
    source,
  })
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(
    null,
  )

  // Default to latest when analyses load for the first time
  useEffect(() => {
    if (analyses.length > 0 && !selectedAnalysisId) {
      setSelectedAnalysisId(analyses[0].id)
    }
  }, [analyses, selectedAnalysisId])

  // Выбор остаётся, только если версия ещё есть в (отфильтрованной) истории;
  // иначе — последняя версия списка (смена source-фильтра, §1.2).
  const analysisId = analyses.some((a) => a.id === selectedAnalysisId)
    ? selectedAnalysisId
    : (analyses[0]?.id ?? null)

  const {
    data: stats,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAnalysisQuery(analysisId!, { skip: !analysisId })

  const fmtDate = (iso: string) => {
    const d = new Date(iso)
    return isNaN(d.getTime()) ? iso : format(d, "dd.MM.yyyy", { locale: ru })
  }

  const chatTypeLabel = (type: string) => {
    if (type === "channel") return "Канал"
    if (type === "supergroup" || type === "group") return "Группа"
    if (type === "private_channel") return "Приватный канал"
    if (type === "private_supergroup" || type === "private_group")
      return "Приватная группа"
    return type
  }

  // True when no data yet (initial load or chat switch before analysisId resolves)
  const showFullSpinner = isLoading || !analysisId

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">{chat.name}</Typography>
              <Chip
                label={chatTypeLabel(chat.type)}
                size="small"
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {chat.memberCount.toLocaleString("ru-RU")} участников
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isFetching && !showFullSpinner && <CircularProgress size={16} />}
            <SourceFilterChips source={source} onChange={setSource} />
            <AnalysisVersionSelect
              chatId={chat.id}
              // Производный id (не сырой selectedAnalysisId): при смене
              // source-фильтра селект показывает выпавшую версию (§1.2).
              selectedAnalysisId={analysisId}
              onChange={setSelectedAnalysisId}
              source={source}
            />
          </Stack>
        </Stack>

        {showFullSpinner && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => refetch()}>
                Повторить
              </Button>
            }
          >
            Ошибка загрузки анализа
          </Alert>
        )}

        {stats && (
          <>
            {/* Meta */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Table
                size="small"
                sx={{ "& td": { border: 0, py: 0.25, px: 1 } }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", width: 120 }}>
                      Период
                    </TableCell>
                    <TableCell>
                      {fmtDate(stats.dateMin)} — {fmtDate(stats.dateMax)}{" "}
                      <Typography
                        component="span"
                        color="text.secondary"
                        variant="caption"
                      >
                        ({stats.totalDays} дн.)
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", width: 120 }}>
                      Сообщений
                    </TableCell>
                    <TableCell>{stats.total.toLocaleString("ru-RU")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            <SectionPaper title="🔗 Пересечения с другими чатами">
              <IntersectionSummary
                selectedChatId={chat.id}
                selectedChatName={chat.name}
                source={librarySource}
              />
            </SectionPaper>

            <SectionPaper
              title="📌 Ключевые показатели"
              hint="Зелёный — хорошо, жёлтый — стоит проверить, красный — тревожный сигнал."
            >
              <KpiGrid stats={stats} />
            </SectionPaper>

            <SectionPaper
              title="🔍 Оценка накрученности (Fraud Score)"
              hint="0–20: органичный. 20–40: вопросы. 40–65: высокий риск. 65+: накрутка."
            >
              <FraudScore stats={stats} />
            </SectionPaper>

            {/* Charts grid */}
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
                minWidth: 0,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <SectionPaper
                  title="📅 Активность по дням недели"
                  hint={(() => {
                    const weekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]
                    const bestIdx = Object.entries(stats.weekdayMsgs).sort(
                      (a, b) => Number(b[1]) - Number(a[1]),
                    )[0]?.[0]
                    return `Лучший день для рекламы: ${bestIdx !== undefined ? weekdays[Number(bestIdx)] : "—"}`
                  })()}
                >
                  <WeekdayBarChart stats={stats} />
                </SectionPaper>
              </Box>
              <Box
                sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <SectionPaper
                  title="⏰ Активность по часам суток"
                  hint="Выделенный столбец — пик активности. Публикуйте рекламу за 30–60 минут ДО пика."
                >
                  <HourlyBarChart stats={stats} />
                </SectionPaper>
              </Box>
            </Box>

            <SectionPaper
              title="📆 Динамика активности по неделям"
              hint="Если активность падает неделю к неделе на >30% — аудитория «умирает». Стабильность или рост — хороший сигнал."
            >
              <WeeklyTrendChart stats={stats} />
            </SectionPaper>

            {/* Top days + Content types */}
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                minWidth: 0,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <SectionPaper
                  title="🏅 Топ-5 активных дней"
                  hint="Помогает понять пиковые события."
                >
                  <Table size="small">
                    <TableBody>
                      {stats.topDays.map((d, i) => {
                        const weekdays = [
                          "Пн",
                          "Вт",
                          "Ср",
                          "Чт",
                          "Пт",
                          "Сб",
                          "Вс",
                        ]
                        const dow = new Date(d.date).getDay()
                        const label = weekdays[dow === 0 ? 6 : dow - 1]
                        return (
                          <TableRow
                            key={d.date}
                            sx={{ "&:last-child td": { border: 0 } }}
                          >
                            <TableCell
                              sx={{ color: "text.secondary", width: 30 }}
                            >
                              {i + 1}
                            </TableCell>
                            <TableCell>
                              <strong>{d.date}</strong>{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                ({label})
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <strong>{d.count.toLocaleString("ru-RU")}</strong>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </SectionPaper>
              </Box>
              <Box
                sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
              >
                <SectionPaper
                  title="🎨 Типы контента"
                  hint="20–50% медиа — норма живого чата. >80% медиа — скорее канал пересылок."
                >
                  <ContentTypesTable stats={stats} />
                </SectionPaper>
              </Box>
            </Box>

            <SectionPaper
              title="❤️ Реакции и вовлечённость"
              hint="Реакции/сообщение (ER) — показатель вовлечённости. Для городских чатов нормальный ER: 0.03–0.15."
            >
              <ReactionsSection stats={stats} />
            </SectionPaper>

            <SectionPaper
              title="🏆 Топ-30 авторов"
              hint="🤖 — бот или канал. ⚠️ — доля >20%."
            >
              <AuthorsTable stats={stats} />
            </SectionPaper>

            <SectionPaper title="📋 Сводная таблица для принятия решения">
              <SummaryTable stats={stats} />
            </SectionPaper>

            <SectionPaper
              title="📚 Справочник показателей"
              hint="Нажмите на название показателя, чтобы раскрыть объяснение."
            >
              <MetricsGlossary />
            </SectionPaper>

            {stats.reportDate &&
              !isNaN(new Date(stats.reportDate).getTime()) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="right"
                >
                  Отчёт:{" "}
                  {format(new Date(stats.reportDate), "dd.MM.yyyy HH:mm", {
                    locale: ru,
                  })}
                </Typography>
              )}
          </>
        )}
      </Stack>
    </Box>
  )
}

// ── EmptyState ────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 1,
        p: 4,
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Выберите чат
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        Выберите чат из списка слева или добавьте новый через «Добавить чат»
      </Typography>
    </Box>
  )
}

// ── ChatAnalyzePage ───────────────────────────────────────────────────────────

export function ChatAnalyzePage() {
  const [selectedChat, setSelectedChat] =
    useState<ChatAnalyze.SavedChat | null>(null)
  // Source-фильтр библиотеки (дельта 2026-09-22 §1.1): общий для списка чатов
  // и для кандидатов пересечений — результаты согласованы с видимым списком (§1.3.3).
  const [librarySource, setLibrarySource] = useState<string | undefined>(
    undefined,
  )
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const handleSelect = (chat: ChatAnalyze.SavedChat) => {
    setSelectedChat(chat)
    setMobileDrawerOpen(false) // auto-close on mobile after selection
  }

  const libraryPanel = (
    <ChatLibraryPanel
      selectedChatId={selectedChat?.id ?? null}
      source={librarySource}
      onSourceChange={setLibrarySource}
      onSelect={handleSelect}
    />
  )

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
      maxWidth={false}
    >
      {/* Page header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {isMobile && (
          <IconButton
            size="small"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Открыть список чатов"
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box>
          <Typography variant="h5" component="h1">
            Анализ Telegram-чатов
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Библиотека сохранённых чатов и анализ аудитории
          </Typography>
        </Box>
      </Box>

      {/* Split panel */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Desktop: persistent left panel */}
        {!isMobile && libraryPanel}

        {/* Mobile: collapsible drawer */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            PaperProps={{ sx: { width: 280 } }}
          >
            {libraryPanel}
          </Drawer>
        )}

        {/* Right panel — key resets all local state (selectedAnalysisId etc.) on chat switch */}
        {selectedChat ? (
          <ChatDetailPanel
            key={selectedChat.id}
            chat={selectedChat}
            librarySource={librarySource}
          />
        ) : (
          <EmptyState />
        )}
      </Box>
    </Container>
  )
}
